import * as React from 'react';
import { memo, useCallback } from 'react';

import { Badge, Icon } from '@rbilabs/universal-components';
import { StyleSheet } from 'react-native';

import Picture from 'components/picture';
import { Box } from 'components/ui';
import { RemoveRewardButton } from 'pages/loyalty/loyalty-rewards/remove-reward-button';
import { selectors, useAppSelector } from 'state/global-state';
import { t } from 'state/intl/translations';
import { RewardRedemptionMethod } from 'state/loyalty/hooks/types';
import { useRewardRedemptionLimit } from 'state/loyalty/hooks/use-reward-redemption-limit';
import { LoyaltyReward } from 'state/loyalty/types';
import { primitive } from 'styles/constants';
import { TraceName, stopTrace } from 'utils/performance/trace';

import { incentiveCardImageDefaultProps } from '../constants';

import { IncentiveCardInRestaurantIncrementor } from './incentive-card-in-restaurant-incrementor';
import { IncentiveCardIncrementor } from './incentive-card-incrementor';
import {
  AddIconButton,
  AlertIcon,
  BackgroundImage,
  CardContainer,
  ImageWrapper,
  InfoContainer,
  InlineAlertContainer,
  InlineAlertSpacer,
  InlineAlertText,
  RewardText,
  StyledClickableContainer,
  StyledRewardLock,
} from './incentive-card.styled';
import { RewardCardIncentive, parseIncentiveData } from './parse-incentive-data';

const styles = StyleSheet.create({
  appliedBadgeContainer: {
    position: 'absolute',
    right: 4,
    top: 4,
    zIndex: 1,
  },
});

export interface IIncentiveCard<T extends LoyaltyReward | RewardCardIncentive = LoyaltyReward> {
  incentive: T;
  handlePress: (reward: T) => void;
  locked?: boolean;
  inlineAlertMessage?: React.ReactNode;
  isPictureBlurhashEnabled?: boolean;
  isApplied?: boolean;
  inCooldown?: boolean;
}

const IncentiveCardComponent = <T extends LoyaltyReward | RewardCardIncentive>({
  incentive,
  handlePress,
  locked = false,
  inlineAlertMessage,
  isPictureBlurhashEnabled = true,
  isApplied = false,
  inCooldown = false,
}: React.PropsWithChildren<IIncentiveCard<T>>) => {
  const rewardRedemptionLimit = useRewardRedemptionLimit();
  const appliedRewardsArray = useAppSelector(selectors.loyalty.selectAppliedLoyaltyRewardsArray);
  const incentiveData =
    '_id' in incentive
      ? parseIncentiveData(incentive)
      : parseIncentiveData(incentive as RewardCardIncentive);

  const { imageDescription, name } = incentiveData;
  const image = incentiveData.image as React.ComponentProps<typeof Picture>['image'];
  const backgroundImage = incentiveData.backgroundImage as React.ComponentProps<
    typeof BackgroundImage
  >['image'];
  const rewardId = '_id' in incentive ? incentive._id : incentive.id;

  const onDisplay = useCallback(() => stopTrace(TraceName.REWARDS_LOAD), []);

  const appliedReward = appliedRewardsArray?.find(reward =>
    '_id' in incentive ? reward.sanityId === rewardId : reward.rewardId === rewardId
  );
  const appliedRewardControlId = appliedReward?.sanityId || rewardId;
  const isInRestaurant =
    isApplied && appliedReward?.redemptionMethod === RewardRedemptionMethod.IN_RESTAURANT;

  const renderAppliedControl = () => {
    if (rewardRedemptionLimit > 1) {
      return isInRestaurant ? (
        <IncentiveCardInRestaurantIncrementor rewardId={appliedRewardControlId} />
      ) : (
        <IncentiveCardIncrementor
          rewardId={appliedRewardControlId}
          maxQuantity={rewardRedemptionLimit}
        />
      );
    }
    return <RemoveRewardButton rewardId={appliedRewardControlId} />;
  };

  return (
    <CardContainer>
      <StyledClickableContainer
        isApplied={isApplied}
        isLocked={locked || !!inlineAlertMessage}
        onPress={() =>
          !locked && ('_id' in incentive || !inlineAlertMessage) && !isApplied && !inCooldown
            ? handlePress(incentive)
            : null
        }
      >
        {isApplied && (
          <Box testID={`incentive-card-applied-${rewardId}`} style={styles.appliedBadgeContainer}>
            <Badge
              variant="default-subtle"
              iconName="checkFill"
              backgroundColor={primitive.$successLight}
              _text={{ fontSize: '2xs' }}
              iconSize="3"
              paddingLeft="1"
              paddingRight="1"
            >
              {t('applied', 'Applied')}
            </Badge>
          </Box>
        )}
        <InfoContainer>
          {image ? (
            <ImageWrapper>
              {backgroundImage && <BackgroundImage alt={name} image={backgroundImage} />}
              <Picture
                isBlurhashEnabled={isPictureBlurhashEnabled}
                image={image}
                alt={imageDescription}
                onDisplay={onDisplay}
                {...incentiveCardImageDefaultProps}
              />
            </ImageWrapper>
          ) : null}
          <RewardText>{name}</RewardText>
        </InfoContainer>
        {locked ? (
          <StyledRewardLock accessibilityLabel={t('locked')} />
        ) : (
          <>
            {inlineAlertMessage ? (
              <>
                <InlineAlertSpacer></InlineAlertSpacer>
                <InlineAlertContainer>
                  <AlertIcon />
                  <InlineAlertText>{inlineAlertMessage}</InlineAlertText>
                </InlineAlertContainer>
              </>
            ) : (
              <>
                {isApplied ? (
                  renderAppliedControl()
                ) : (
                  <AddIconButton
                    variant="outline"
                    onPress={() => handlePress(incentive)}
                    icon={<Icon variant="add" />}
                    disabled={locked}
                    testID="add-to-cart"
                    accessibilityLabel={t('addToCart')}
                  />
                )}
              </>
            )}
          </>
        )}
      </StyledClickableContainer>
    </CardContainer>
  );
};

export const IncentiveCard = memo(IncentiveCardComponent) as typeof IncentiveCardComponent;
